import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { CatalogItem } from "../../../types";
import { publishCatalogItem } from "../../../redux/catalog";
import { ConvertibleProduct } from "./CatalogUnpublishedView";

export type PublishCatalogPopupStep =
    | "epoWarning"
    | "upgradeEpo"
    | "publishConfirm"
    | "publishSuccess";

export type PublishCatalogPopupMode = "publish" | "upgrade";

export interface PublishCatalogPopupState {
    item: CatalogItem;
    step: PublishCatalogPopupStep;
    mode: PublishCatalogPopupMode;
    convertibleProduct?: ConvertibleProduct;
}

export function usePublishCatalogPopup() {
    const dispatch = useDispatch();
    const [popupState, setPopupState] =
        useState<PublishCatalogPopupState | null>(null);
    const [publishing, setPublishing] = useState(false);

    const closePopup = useCallback(() => {
        setPopupState(null);
        setPublishing(false);
    }, []);

    const goToStep = useCallback((step: PublishCatalogPopupStep) => {
        setPopupState((prev) => (prev ? { ...prev, step } : null));
    }, []);

    const openPublishFlow = useCallback(
        (item: CatalogItem, convertibleProduct?: ConvertibleProduct) => {
            setPopupState({
                item,
                step:
                    item.copied_from !== "ps-products" && convertibleProduct
                        ? "epoWarning"
                        : "publishConfirm",
                mode: "publish",
                convertibleProduct,
            });
        },
        [],
    );

    const openUpgradeFlow = useCallback(
        (item: CatalogItem, convertibleProduct: ConvertibleProduct) => {
            setPopupState({
                item,
                step: "upgradeEpo",
                mode: "upgrade",
                convertibleProduct,
            });
        },
        [],
    );

    const onEpoWarningCancel = useCallback(() => {
        goToStep("publishConfirm");
    }, [goToStep]);

    const onEpoWarningUpgrade = useCallback(() => {
        goToStep("upgradeEpo");
    }, [goToStep]);

    const onPublishWithoutUpgrade = useCallback(() => {
        goToStep("publishConfirm");
    }, [goToStep]);

    const onPublishConfirmCancel = closePopup;

    const onPublishConfirm = useCallback(async () => {
        if (!popupState || publishing) {
            return;
        }

        setPublishing(true);
        const result = await dispatch(
            publishCatalogItem(popupState.item.item_id) as any,
        );
        setPublishing(false);

        if (publishCatalogItem.fulfilled.match(result)) {
            goToStep("publishSuccess");
        }
    }, [dispatch, goToStep, popupState, publishing]);

    const onPublishSuccessDone = closePopup;

    return {
        popupState,
        publishing,
        openPublishFlow,
        openUpgradeFlow,
        closePopup,
        onEpoWarningCancel,
        onEpoWarningUpgrade,
        onPublishWithoutUpgrade,
        onPublishConfirmCancel,
        onPublishConfirm,
        onPublishSuccessDone,
    };
}
